import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

export default function Users() {
  const [users, setUsers]       = useState([]);
  const [roles, setRoles]       = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [manageRoles, setManageRoles] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState('');
  const { user: me, apiToken }  = useAuth();
  const token = apiToken || localStorage.getItem('cafepos_token');

  const roleMap = Object.fromEntries(roles.map(r => [r.slug, r]));

  const load = useCallback(async () => {
    const res = await fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } });
    setUsers(await res.json());
  }, [token]);

  const loadRoles = useCallback(async () => {
    const res = await fetch('/api/roles', { headers: { Authorization: `Bearer ${token}` } });
    setRoles(await res.json());
  }, [token]);

  useEffect(() => { load(); loadRoles(); }, [load, loadRoles]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 2500); }

  async function saveUser(form) {
    setSaving(true);
    try {
      if (form.id) {
        const body = { role: form.role, active: form.active,
          first_name: form.first_name, last_name: form.last_name,
          phone: form.phone, email: form.email, address: form.address,
          hourly_rate: form.hourly_rate || null,
        };
        if (form.name) body.name = form.name;
        if (form.password) body.password = form.password;
        if (form.pin) body.pin = form.pin;
        const res = await fetch(`/api/users/${form.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        showToast('Zapisano zmiany');
      } else {
        const res = await fetch('/api/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: form.name, password: form.password, role: form.role, ...(form.pin ? { pin: form.pin } : {}), first_name: form.first_name, last_name: form.last_name, phone: form.phone, email: form.email, address: form.address, hourly_rate: form.hourly_rate || null }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        showToast(`Dodano użytkownika "${form.name}"`);
      }
      setEditUser(null);
      load();
    } catch (err) { showToast('Błąd: ' + err.message); }
    finally { setSaving(false); }
  }

  async function deleteUser(user) {
    if (!window.confirm(`Usunąć użytkownika "${user.name}"?`)) return;
    const res = await fetch(`/api/users/${user.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) { showToast('Błąd: ' + data.error); return; }
    showToast(`Usunięto "${user.name}"`);
    load();
  }

  async function toggleActive(user) {
    const res = await fetch(`/api/users/${user.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ active: user.active ? 0 : 1 }),
    });
    const data = await res.json();
    if (!res.ok) { showToast('Błąd: ' + data.error); return; }
    showToast(user.active ? `"${user.name}" dezaktywowano` : `"${user.name}" aktywowano`);
    load();
  }

  return (
    <div style={{ padding: 24, maxWidth: 700 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 500 }}>Użytkownicy</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{users.length} kont</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-outline" style={{ width: 'auto', padding: '9px 16px' }} onClick={() => setManageRoles(true)}>
            🏷️ Zarządzaj rolami
          </button>
          <button className="btn-primary" style={{ width: 'auto', padding: '9px 16px' }}
            onClick={() => setEditUser({ name: '', password: '', role: roles[0]?.slug || 'waiter', active: 1, pin: '', first_name: '', last_name: '', phone: '', email: '', address: '', hourly_rate: '' })}>
            + Nowy użytkownik
          </button>
        </div>
      </div>

      <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        {users.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
            Brak użytkowników
          </div>
        )}
        {users.map((user, i) => {
          const rc = roleMap[user.role] || { color_bg: 'var(--blue-light)', color_text: 'var(--blue-dark)', label: user.role, icon: '👤' };
          const isMe = user.id === me?.id;
          return (
            <div key={user.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px',
              borderBottom: i < users.length - 1 ? '0.5px solid var(--border)' : 'none',
              opacity: user.active ? 1 : 0.5,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: rc.color_bg, color: rc.color_text,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 600, fontSize: 14, flexShrink: 0,
              }}>
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  {user.first_name || user.last_name
                    ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                    : user.name}
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 }}>({user.name})</span>
                  {isMe && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>(ty)</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                  {user.phone && <span style={{ marginRight: 8 }}>📞 {user.phone}</span>}
                  {user.hourly_rate && <span>💰 {Number(user.hourly_rate).toFixed(2)} zł/h</span>}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 3, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, background: rc.color_bg, color: rc.color_text, padding: '1px 7px', borderRadius: 4, fontWeight: 500 }}>
                    {rc.icon} {rc.label}
                  </span>
                  {!user.active && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>nieaktywny</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn-sm" onClick={() => setEditUser({ id: user.id, name: user.name, password: '', role: user.role, active: user.active, pin: '', first_name: user.first_name||'', last_name: user.last_name||'', phone: user.phone||'', email: user.email||'', address: user.address||'', hourly_rate: user.hourly_rate||'' })}>
                  Edytuj
                </button>
                <button className={`btn-sm ${user.active ? 'danger' : 'success'}`} onClick={() => toggleActive(user)} disabled={isMe}>
                  {user.active ? 'Wyłącz' : 'Włącz'}
                </button>
                <button className="btn-sm danger" onClick={() => deleteUser(user)} disabled={isMe}>
                  Usuń
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {editUser && (
        <UserModal form={editUser} roles={roles} onSave={saveUser} onClose={() => setEditUser(null)} saving={saving} />
      )}
      {manageRoles && (
        <RoleManagerModal roles={roles} token={token} onClose={() => setManageRoles(false)}
          onChanged={() => { loadRoles(); load(); }} showToast={showToast} />
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function RoleSelect({ value, onChange, roles }) {
  const [open, setOpen] = React.useState(false);
  const selected = roles.find(r => r.slug === value) || roles[0] || { icon: '👤', label: value };

  if (!roles.length) return null;

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-sm)',
        border: '0.5px solid var(--border)', background: 'var(--bg)',
        color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
      }}>
        <span>{selected.icon}</span>
        <span style={{ flex: 1 }}>{selected.label}</span>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>▼</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'var(--surface)', border: '0.5px solid var(--border)',
          borderRadius: 'var(--radius-sm)', marginTop: 4, overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          {roles.map(r => (
            <button key={r.slug} type="button" onClick={() => { onChange(r.slug); setOpen(false); }}
              style={{
                width: '100%', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8,
                background: r.slug === value ? 'var(--blue-light)' : 'none',
                border: 'none', cursor: 'pointer', fontSize: 14,
                color: r.slug === value ? 'var(--blue)' : 'var(--text-primary)',
                textAlign: 'left',
              }}>
              <span>{r.icon}</span>
              <span>{r.label}</span>
              {r.slug === value && <span style={{ marginLeft: 'auto', fontSize: 12 }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UserModal({ form: initial, roles, onSave, onClose, saving }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', address: '', hourly_rate: '', ...initial });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isNew = !initial.id;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 380, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500 }}>{isNew ? 'Nowy użytkownik' : `Edytuj: ${initial.name}`}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>

        <div className="field">
          <label>Nazwa użytkownika</label>
          <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="np. Ania, Piotr" />
        </div>

        <div className="field">
          <label>{isNew ? 'Hasło' : 'Nowe hasło'} <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>{!isNew && '(zostaw puste żeby nie zmieniać)'}</span></label>
          <input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="min. 6 znaków" />
        </div>
        <div className="field">
          <label>PIN terminala <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(4 cyfry, opcjonalny)</span></label>
          <input type="password" maxLength={4} value={form.pin || ''} onChange={e => set('pin', e.target.value.replace(/\D/g,'').slice(0,4))} placeholder="np. 1234" />
        </div>

        <div className="field">
          <label>Rola</label>
          <RoleSelect value={form.role} onChange={v => set('role', v)} roles={roles} />
        </div>

        <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 16, marginTop: 4, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 12 }}>Dane pracownika</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div className="field" style={{ flex: 1, margin: 0 }}>
              <label>Imię</label>
              <input value={form.first_name || ''} onChange={e => set('first_name', e.target.value)} placeholder="np. Jan" />
            </div>
            <div className="field" style={{ flex: 1, margin: 0 }}>
              <label>Nazwisko</label>
              <input value={form.last_name || ''} onChange={e => set('last_name', e.target.value)} placeholder="np. Kowalski" />
            </div>
          </div>
          <div className="field" style={{ marginTop: 8 }}>
            <label>Telefon</label>
            <input value={form.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="np. 500 000 000" />
          </div>
          <div className="field">
            <label>Email</label>
            <input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} placeholder="np. jan@example.com" />
          </div>
          <div className="field">
            <label>Adres zamieszkania</label>
            <input value={form.address || ''} onChange={e => set('address', e.target.value)} placeholder="np. ul. Kwiatowa 1, 00-001 Warszawa" />
          </div>
          <div className="field">
            <label>Stawka godzinowa (zł/h)</label>
            <input type="number" min="0" step="0.01" value={form.hourly_rate || ''} onChange={e => set('hourly_rate', e.target.value)} placeholder="np. 25.00" />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className="btn-outline" onClick={onClose} style={{ flex: 1 }}>Anuluj</button>
          <button className="btn-primary" style={{ flex: 2 }}
            onClick={() => onSave(form)}
            disabled={saving || !form.name || (isNew && !form.password)}>
            {saving ? 'Zapisywanie...' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  );
}

const COLOR_PRESETS = [
  { bg: '#FCEBEB', text: '#A32D2D' },
  { bg: '#EBF3FC', text: '#185FA5' },
  { bg: '#E4EEFB', text: '#1D4E89' },
  { bg: '#EAF3DE', text: '#3B6D11' },
  { bg: '#F0EBF8', text: '#6B3FA0' },
  { bg: '#FFF4E0', text: '#8A5A00' },
  { bg: '#E0F7F4', text: '#0F766E' },
];

function RoleManagerModal({ roles, token, onClose, onChanged, showToast }) {
  const [editRole, setEditRole] = useState(null); // null = lista, {} = nowa, {id,...} = edycja
  const [saving, setSaving]     = useState(false);

  async function saveRole(form) {
    setSaving(true);
    try {
      if (form.id) {
        const res = await fetch(`/api/roles/${form.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ label: form.label, color_bg: form.color_bg, color_text: form.color_text, icon: form.icon }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        showToast('Zapisano rolę');
      } else {
        const res = await fetch('/api/roles', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(form),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        showToast(`Dodano rolę "${form.label}"`);
      }
      setEditRole(null);
      onChanged();
    } catch (err) { showToast('Błąd: ' + err.message); }
    finally { setSaving(false); }
  }

  async function deleteRole(role) {
    if (!window.confirm(`Usunąć rolę "${role.label}"?`)) return;
    const res = await fetch(`/api/roles/${role.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    if (!res.ok) { showToast('Błąd: ' + data.error); return; }
    showToast(`Usunięto rolę "${role.label}"`);
    onChanged();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 420, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500 }}>Zarządzaj rolami</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>

        {!editRole && (
          <>
            <div style={{ border: '0.5px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
              {roles.map((r, i) => (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  borderBottom: i < roles.length - 1 ? '0.5px solid var(--border)' : 'none',
                }}>
                  <span style={{ fontSize: 11, background: r.color_bg, color: r.color_text, padding: '3px 9px', borderRadius: 4, fontWeight: 500 }}>
                    {r.icon} {r.label}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1 }}>{r.slug}</span>
                  <button className="btn-sm" onClick={() => setEditRole({ ...r })}>Edytuj</button>
                  <button className="btn-sm danger" onClick={() => deleteRole(r)}>Usuń</button>
                </div>
              ))}
              {roles.length === 0 && (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Brak ról</div>
              )}
            </div>
            <button className="btn-primary" style={{ width: '100%' }}
              onClick={() => setEditRole({ slug: '', label: '', color_bg: COLOR_PRESETS[0].bg, color_text: COLOR_PRESETS[0].text, icon: '👤' })}>
              + Nowa rola
            </button>
          </>
        )}

        {editRole && (
          <RoleForm role={editRole} saving={saving} onCancel={() => setEditRole(null)} onSave={saveRole} />
        )}
      </div>
    </div>
  );
}

function RoleForm({ role, saving, onCancel, onSave }) {
  const [form, setForm] = useState(role);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isNew = !form.id;

  return (
    <div>
      {isNew && (
        <div className="field">
          <label>Identyfikator (slug) <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>tylko małe litery, cyfry, _</span></label>
          <input value={form.slug} onChange={e => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} placeholder="np. kierownik_zmiany" />
        </div>
      )}
      <div className="field">
        <label>Nazwa wyświetlana</label>
        <input value={form.label} onChange={e => set('label', e.target.value)} placeholder="np. Kierownik zmiany" />
      </div>
      <div className="field">
        <label>Ikona (emoji)</label>
        <input value={form.icon} onChange={e => set('icon', e.target.value)} placeholder="np. 🧑‍💼" maxLength={4} />
      </div>
      <div className="field">
        <label>Kolor</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {COLOR_PRESETS.map((c, i) => (
            <button key={i} type="button" onClick={() => { set('color_bg', c.bg); set('color_text', c.text); }}
              style={{
                width: 32, height: 32, borderRadius: '50%', background: c.bg,
                border: form.color_bg === c.bg ? `2px solid ${c.text}` : '2px solid transparent',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
              {form.color_bg === c.bg && <span style={{ color: c.text, fontSize: 14 }}>✓</span>}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="btn-outline" onClick={onCancel} style={{ flex: 1 }}>Anuluj</button>
        <button className="btn-primary" style={{ flex: 2 }}
          onClick={() => onSave(form)}
          disabled={saving || !form.label || (isNew && !form.slug)}>
          {saving ? 'Zapisywanie...' : 'Zapisz'}
        </button>
      </div>
    </div>
  );
}
